// File: /js/shared/components/a2hsOnboarding.js
//
// iOS Add-to-Home-Screen onboarding (App P1 — PWA foundation, 2026-07).
//
// iOS Safari never fires `beforeinstallprompt`: installing the PWA is a
// manual Share → "Sur l'écran d'accueil" gesture the visitor has to be
// told about. This component shows a small dismissible bottom card with
// that instruction, ONLY when all gates pass:
//   - iOS device (iPhone/iPod/iPad, incl. iPadOS 13+ desktop-UA mode),
//   - Safari proper (third-party iOS browsers can't A2HS from the share
//     sheet outside the EU),
//   - not already running standalone (installed),
//   - not previously dismissed (localStorage, permanent).
//
// The dismiss button registers on the existing `data-ui` registry
// (§1.3 rule-9 carve-out: registering on an existing resolver is REUSE,
// not a new document seam). Strings come from the JsConfig i18n bag
// (`pwa.a2hs.*` — declared in JsConfig::I18N_KEYS, ×4 locales).

import { i18n } from '/js/shared/utils/config.js';
import { registerUiHandler } from '/js/shared/extensions/domExtensions.js';

const DISMISS_KEY = 'lbdd.a2hs.dismissed';
const OVERLAY_ID = 'a2hs-onboarding';
const SHOW_DELAY_MS = 2500;

// The iOS share glyph (square with up arrow), inline so the card needs no
// asset fetch; inherits currentColor.
const SHARE_ICON_SVG =
  '<svg class="a2hs-onboarding__share-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">'
  + '<g fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">'
  + '<path d="M12 3v12"/><path d="M8 6.5 12 3l4 3.5"/>'
  + '<path d="M7 10H5.5v10h13V10H17"/>'
  + '</g></svg>';

function isIosDevice() {
  const ua = navigator.userAgent;
  if (/iphone|ipod|ipad/i.test(ua)) return true;
  // iPadOS 13+ masquerades as macOS but is the only Mac with touch points.
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

function isIosSafari() {
  const ua = navigator.userAgent;
  return /safari/i.test(ua) && !/crios|fxios|edgios|opios|opt\/|chrome|android/i.test(ua);
}

function isStandalone() {
  return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    || window.navigator.standalone === true;
}

function isDismissed() {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; }
  catch { return false; }
}

registerUiHandler('click', 'a2hs-dismiss', () => {
  try { localStorage.setItem(DISMISS_KEY, '1'); }
  catch { /* private mode — the card simply reappears next visit */ }
  const overlay = document.getElementById(OVERLAY_ID);
  if (overlay) overlay.remove();
});

function buildOverlay() {
  const overlay = document.createElement('div');
  overlay.id = OVERLAY_ID;
  overlay.className = 'a2hs-onboarding';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-label', i18n('pwa.a2hs.title'));
  overlay.innerHTML =
    '<div class="a2hs-onboarding__card">'
    + '<p class="a2hs-onboarding__title">' + i18n('pwa.a2hs.title') + '</p>'
    + '<p class="a2hs-onboarding__body">' + i18n('pwa.a2hs.body', { share: SHARE_ICON_SVG }) + '</p>'
    + '<button type="button" class="a2hs-onboarding__dismiss" data-ui="a2hs-dismiss">'
    + i18n('pwa.a2hs.dismiss')
    + '</button>'
    + '</div>';
  return overlay;
}

export function initA2hsOnboarding() {
  if (!isIosDevice() || !isIosSafari() || isStandalone() || isDismissed()) return;
  setTimeout(() => {
    if (document.getElementById(OVERLAY_ID)) return;
    document.body.appendChild(buildOverlay());
  }, SHOW_DELAY_MS);
}
